<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import AgentCapacityPolicyCard from 'dashboard/components-next/AssignmentPolicy/AgentCapacityPolicyCard/AgentCapacityPolicyCard.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConfirmDeletePolicyDialog from './components/ConfirmDeletePolicyDialog.vue';
import PolicyPageHeader from './components/PolicyPageHeader.vue';
import PolicyEmptyState from './components/PolicyEmptyState.vue';
import { filterPolicies } from 'dashboard/components-next/AssignmentPolicy/helpers';
import { getInboxIconByType } from 'dashboard/helper/inbox';

const store = useStore();
const { t } = useI18n();
const router = useRouter();

const agentCapacityPolicies = useMapGetter(
  'agentCapacityPolicies/getAgentCapacityPolicies'
);
const uiFlags = useMapGetter('agentCapacityPolicies/getUIFlags');
const usersUiFlags = useMapGetter('agentCapacityPolicies/getUsersUIFlags');

const confirmDeletePolicyDialogRef = ref(null);

const searchQuery = ref('');

const filteredPolicies = computed(() =>
  filterPolicies(agentCapacityPolicies.value, searchQuery.value)
);

const breadcrumbItems = computed(() => [
  {
    label: t('ASSIGNMENT_POLICY.INDEX.HEADER.TITLE'),
    routeName: 'assignment_policy_index',
  },
  { label: t('ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.INDEX.HEADER.TITLE') },
]);

const inboxes = useMapGetter('inboxes/getAllInboxes');

const inboxLookup = computed(() =>
  Object.fromEntries(
    (inboxes.value || []).map(inbox => [
      inbox.id,
      {
        name: inbox.name,
        icon: getInboxIconByType(
          inbox.channelType,
          inbox.medium,
          'line',
          inbox.voiceEnabled
        ),
      },
    ])
  )
);

const handleBreadcrumbClick = item => {
  router.push({
    name: item.routeName,
  });
};

const onClickCreatePolicy = () => {
  router.push({
    name: 'agent_capacity_policy_create',
  });
};

const onClickEditPolicy = id => {
  router.push({
    name: 'agent_capacity_policy_edit',
    params: {
      id,
    },
  });
};

const handleFetchUsers = id => {
  if (usersUiFlags.value.isFetching) return;
  store.dispatch('agentCapacityPolicies/getUsers', id);
};

const handleDelete = id => {
  const policy = agentCapacityPolicies.value.find(item => item.id === id);
  confirmDeletePolicyDialogRef.value.openDialog(id, policy?.name);
};

const handleDeletePolicy = async policyId => {
  try {
    await store.dispatch('agentCapacityPolicies/delete', policyId);
    useAlert(
      t('ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.DELETE_POLICY.SUCCESS_MESSAGE')
    );
    confirmDeletePolicyDialogRef.value.closeDialog();
  } catch (error) {
    useAlert(
      t('ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.DELETE_POLICY.ERROR_MESSAGE')
    );
  }
};

onMounted(() => {
  store.dispatch('agentCapacityPolicies/get');
});
</script>

<template>
  <SettingsLayout :is-loading="uiFlags.isFetching">
    <template #header>
      <PolicyPageHeader
        icon="i-hugeicons-dashboard-speed-02"
        :title="
          $t('ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.INDEX.HEADER.TITLE')
        "
        :description="$t('ASSIGNMENT_POLICY.REDESIGN.CAPACITY.LIST_INTRO')"
        :breadcrumbs="breadcrumbItems"
        @breadcrumb-click="handleBreadcrumbClick"
      >
        <template #actions>
          <Button
            icon="i-hugeicons-add-01"
            :label="
              $t(
                'ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.INDEX.HEADER.CREATE_POLICY'
              )
            "
            data-test-id="policy-create"
            @click="onClickCreatePolicy"
          />
        </template>
        <Input
          v-if="agentCapacityPolicies.length"
          v-model="searchQuery"
          class="w-full sm:max-w-sm"
          :placeholder="
            $t('ASSIGNMENT_POLICY.REDESIGN.COMMON.SEARCH_PLACEHOLDER')
          "
          :aria-label="
            $t('ASSIGNMENT_POLICY.REDESIGN.COMMON.SEARCH_PLACEHOLDER')
          "
        />
        <span
          class="rounded-md bg-n-alpha-2 px-2.5 py-1 text-xs text-n-slate-11 ltr:ml-auto rtl:mr-auto"
        >
          {{
            $t(
              'ASSIGNMENT_POLICY.REDESIGN.COMMON.COUNT',
              { n: agentCapacityPolicies.length },
              agentCapacityPolicies.length
            )
          }}
        </span>
      </PolicyPageHeader>
    </template>
    <template #body>
      <PolicyEmptyState
        v-if="!agentCapacityPolicies.length"
        icon="i-hugeicons-dashboard-speed-02"
        :title="$t('ASSIGNMENT_POLICY.REDESIGN.CAPACITY.EMPTY_TITLE')"
        :description="
          $t('ASSIGNMENT_POLICY.REDESIGN.CAPACITY.EMPTY_DESCRIPTION')
        "
        :action-label="
          $t(
            'ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.INDEX.HEADER.CREATE_POLICY'
          )
        "
        @action="onClickCreatePolicy"
      />
      <p
        v-else-if="!filteredPolicies.length"
        class="py-16 text-center text-sm text-n-slate-11"
      >
        {{ $t('ASSIGNMENT_POLICY.REDESIGN.COMMON.NO_RESULTS') }}
      </p>
      <div v-else class="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
        <AgentCapacityPolicyCard
          v-for="policy in filteredPolicies"
          :key="policy.id"
          v-bind="policy"
          :inbox-lookup="inboxLookup"
          :is-fetching-users="usersUiFlags.isFetching"
          @fetch-users="handleFetchUsers"
          @edit="onClickEditPolicy"
          @delete="handleDelete"
        />
      </div>
    </template>
    <ConfirmDeletePolicyDialog
      ref="confirmDeletePolicyDialogRef"
      @delete="handleDeletePolicy"
    />
  </SettingsLayout>
</template>
